import React from 'react';
import {
  Button,
  Card,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { ReferralItem } from '../../models/referralModel';
import { IconInbox } from '@tabler/icons-react';
import {
  renderAccountCreatedBadge,
  renderLineBadge,
  renderStatusBadge,
} from './referralBadgeStyles';

interface Props {
  prospects: ReferralItem[];
  isLoading: boolean;
  onUpdateStatus: (referralId: string, newStatus: string) => void;
  isUpdatingId?: string | null;
  onRegisterProspect?: (item: ReferralItem) => void;
  registeringProspectId?: string | null;
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sep',
      'oct',
      'nov',
      'dic',
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

const ActionBtn: React.FC<{
  label: string;
  variant: 'default' | 'danger' | 'success' | 'primary';
  disabled: boolean;
  onClick: () => void;
}> = ({ label, variant, disabled, onClick }) => {
  const styles = {
    default: { bg: '#ffffff', border: '#cbd5e1', color: '#334155' },
    danger: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
    success: { bg: '#e6f9f3', border: '#99e7cf', color: '#00875e' },
    primary: { bg: '#ede9fe', border: '#c4b5fd', color: '#5b21b6' },
  }[variant];

  return (
    <Button
      size="sm"
      disabled={disabled}
      style={{
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        padding: '3px 10px',
        backgroundColor: styles.bg,
        borderColor: styles.border,
        color: styles.color,
        boxShadow: 'none',
      }}
      onClick={onClick}
    >
      {label}
    </Button>
  );
};

const renderActionButtons = (
  status: string,
  itemId: string,
  line: string,
  isUpdating: boolean,
  onUpdateStatus: (id: string, newStatus: string) => void,
) => {
  if (
    status === 'successful' ||
    status === 'paid' ||
    status === 'does_not_apply' ||
    status === 'cancelled'
  ) {
    const isPositive = status === 'successful' || status === 'paid';
    return (
      <span
        className="d-inline-flex align-items-center font-weight-600"
        style={{
          fontSize: '11px',
          gap: '4px',
          color: isPositive ? '#00875e' : '#64748b',
        }}
      >
        <i
          className={isPositive ? 'fas fa-check-circle' : 'fas fa-times-circle'}
          style={{ fontSize: '10px' }}
        />
        Caso cerrado
      </span>
    );
  }

  const normalizedLine = (line || '').toLowerCase();
  const isBuyer = normalizedLine === 'buyer';
  const isTenant = normalizedLine === 'tenant';
  const isPropertyOrFullService = [
    'supply_sales',
    'supply_rents',
    'full_service',
    'property',
    'external',
  ].includes(normalizedLine);

  const renderButtons = () => {
    switch (status) {
      case 'pending':
        return (
          <>
            <ActionBtn
              label="Contactado"
              variant="default"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'contacted')}
            />
            <ActionBtn
              label="No se pudo contactar"
              variant="danger"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'could_not_contact')}
            />
          </>
        );
      case 'could_not_contact':
        return (
          <ActionBtn
            label="Contacto actualizado"
            variant="default"
            disabled={isUpdating}
            onClick={() => onUpdateStatus(itemId, 'contact_updated')}
          />
        );
      case 'contact_updated':
        if (isPropertyOrFullService) {
          return (
            <>
              <ActionBtn
                label="Se publicó la propiedad"
                variant="success"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'successful')}
              />
              <ActionBtn
                label="No se publicó la propiedad"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
              />
            </>
          );
        }
        return (
          <>
            <ActionBtn
              label="Contactado"
              variant="default"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'contacted')}
            />
            <ActionBtn
              label="No aplica"
              variant="danger"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
            />
          </>
        );
      case 'contacted':
        if (isPropertyOrFullService) {
          // Flujo directo para Propiedades (Supply Sales / Supply Rents) y Full Service
          return (
            <>
              <ActionBtn
                label="Se publicó la propiedad"
                variant="success"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'successful')}
              />
              <ActionBtn
                label="No se publicó la propiedad"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
              />
            </>
          );
        }

        if (isBuyer) {
          return (
            <>
              <ActionBtn
                label="Agendó asesoría"
                variant="default"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'advisory_scheduled')}
              />
              <ActionBtn
                label="No aplica"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
              />
            </>
          );
        }

        return (
          <>
            <ActionBtn
              label="Agendó visita"
              variant="default"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'visit_scheduled')}
            />
            <ActionBtn
              label="No aplica"
              variant="danger"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
            />
          </>
        );
      case 'visit_scheduled':
      case 'advisory_scheduled':
        if (isBuyer) {
          return (
            <>
              <ActionBtn
                label="Reserva confirmada"
                variant="success"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'applies')}
              />
              <ActionBtn
                label="No continuar"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'cancelled')}
              />
            </>
          );
        }

        if (isTenant) {
          return (
            <>
              <ActionBtn
                label="Oferta aprobada"
                variant="success"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'applies')}
              />
              <ActionBtn
                label="Oferta denegada"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'cancelled')}
              />
            </>
          );
        }

        return (
          <>
            <ActionBtn
              label="Aplica"
              variant="success"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'applies')}
            />
            <ActionBtn
              label="No continuar"
              variant="danger"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'cancelled')}
            />
          </>
        );
      case 'applies':
        if (isPropertyOrFullService) {
          return (
            <>
              <ActionBtn
                label="Se publicó la propiedad"
                variant="success"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'successful')}
              />
              <ActionBtn
                label="No se publicó la propiedad"
                variant="danger"
                disabled={isUpdating}
                onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
              />
            </>
          );
        }

        return (
          <>
            <ActionBtn
              label="Exitoso"
              variant="success"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'successful')}
            />
            <ActionBtn
              label="Desistió"
              variant="danger"
              disabled={isUpdating}
              onClick={() => onUpdateStatus(itemId, 'does_not_apply')}
            />
          </>
        );
      default:
        return <span className="text-muted" style={{ fontSize: '12px' }}>—</span>;
    }
  };

  return (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
      {renderButtons()}
    </div>
  );
};

const ReferrerProspectsTable: React.FC<Props> = ({
  prospects,
  isLoading,
  onUpdateStatus,
  isUpdatingId,
  onRegisterProspect,
  registeringProspectId,
}) => {
  return (
    <Card
      className="border-0 mb-0"
      style={{
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardHeader
        className="border-0 bg-white py-3 px-4"
        style={{ borderRadius: '10px 10px 0 0' }}
      >
        <h4 className="font-weight-600 text-dark mb-0" style={{ fontSize: '15px' }}>
          Prospectos referidos
        </h4>
      </CardHeader>

      <Table className="align-items-center table-flush mb-0" responsive hover>
        <thead className="thead-light">
          <tr>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Prospecto
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
                textAlign: 'center',
                width: '115px',
              }}
            >
              Línea
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
                textAlign: 'center',
                width: '135px',
              }}
            >
              Estado
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Acciones
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Responsable
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Fecha
            </th>
            <th scope="col" style={{ fontSize: '11px', width: '60px' }} />
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={7} className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm mr-2"
                  style={{ color: '#00c389' }}
                  role="status"
                />
                <span className="text-muted font-weight-500" style={{ fontSize: '13px' }}>
                  Cargando prospectos...
                </span>
              </td>
            </tr>
          )}

          {!isLoading && prospects.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-5">
                <div className="text-muted">
                  <IconInbox size={36} stroke={1.5} className="mb-2 text-gray" />
                  <p className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>
                    No hay prospectos referidos
                  </p>
                </div>
              </td>
            </tr>
          )}

          {!isLoading &&
            prospects.map((item) => {
              const prospectName =
                `${item.referred_first_name || ''} ${item.referred_last_name || ''}`.trim() ||
                'Sin nombre';
              const prospectPhone = item.referred_phone
                ? `${item.referred_phone_area_code || '+503'} ${item.referred_phone}`
                : '';
              const prospectEmail =
                item.referredProfile?.email || item.data?.email || '';

              const responsibleName = item.assignedResponsible
                ? `${item.assignedResponsible.first_name} ${item.assignedResponsible.last_name}`.trim()
                : '—';

              const isUpdating = isUpdatingId === item.id;

              return (
                <tr key={item.id}>
                  {/* PROSPECTO */}
                  <td>
                    <div className="d-flex align-items-center flex-wrap mb-1" style={{ gap: '6px' }}>
                      <span className="font-weight-600 text-dark" style={{ fontSize: '13px' }}>
                        {prospectName}
                      </span>
                      {item.referred_profile_id && renderAccountCreatedBadge()}
                    </div>
                    {prospectPhone && (
                      <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.3' }}>
                        {prospectPhone}
                      </div>
                    )}
                    {prospectEmail && (
                      <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.3' }}>
                        {prospectEmail}
                      </div>
                    )}
                  </td>

                  {/* LÍNEA */}
                  <td className="text-center">{renderLineBadge(item.line)}</td>

                  {/* ESTADO */}
                  <td className="text-center">
                    {renderStatusBadge(item.status, item.line)}
                  </td>

                  {/* ACCIONES */}
                  <td>
                    {renderActionButtons(
                      item.status,
                      item.id,
                      item.line,
                      isUpdating,
                      onUpdateStatus,
                    )}
                  </td>

                  {/* RESPONSABLE */}
                  <td className="text-dark font-weight-500" style={{ fontSize: '12px' }}>
                    {responsibleName}
                  </td>

                  {/* FECHA */}
                  <td className="text-muted" style={{ fontSize: '12px' }}>
                    {formatDate(item.created_at)}
                  </td>

                  {/* ACCIÓN DE PERFIL / MENÚ */}
                  <td className="text-right">
                    {!item.referred_profile_id && onRegisterProspect && (
                      <UncontrolledDropdown>
                        <DropdownToggle
                          className="btn-icon-only text-light"
                          role="button"
                          size="sm"
                          color=""
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className="fas fa-ellipsis-v text-muted" />
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-arrow" right container="body">
                          <DropdownItem
                            disabled={registeringProspectId === item.id}
                            onClick={() => onRegisterProspect(item)}
                          >
                            {registeringProspectId === item.id
                              ? 'Creando perfil...'
                              : 'Crear perfil al prospecto'}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </Card>
  );
};

export default ReferrerProspectsTable;
